import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { Button, ConfirmationDialog, Loader, RiArrowLeftLine, RiCloseLine, RiMapPinLine } from 'component-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker } from 'react-map-gl';
import { useNavigate } from 'react-router-dom';

import { useGetDraftPlotGroups } from '@/api/rest/resources/plot';
import { DraftPlot, DraftPlotGroup, PlotType } from '@/api/rest/resources/types/plot';
import { withSuspenseBoundary } from '@/components';
import { useOrganization } from '@/hooks/useOrganization';

import { CommitConfirmationDialog } from './components/CommitConfirmationDialog';
import { CoordinatesFooterActions } from './components/CoordinatesFooterActions';
import { CoordinatesReviewPanel } from './components/CoordinatesReviewPanel';
import { DraftPlotsPageLayout } from './components/DraftPlotsPageLayout';
import { EditPlotPanel } from './components/EditPlotPanel';
import { ExcelUploadPanel } from './components/ExcelUploadPanel';
import { GroupEditPanel } from './components/GroupEditPanel';
import { ManualPlotForm } from './components/ManualPlotForm';
import { NewGroupPanel } from './components/NewGroupPanel';
import { OptionsPanel } from './components/OptionsPanel';
import { ProcessingPanel } from './components/ProcessingPanel';
import { PAGE_SIZE } from './constants';
import { useDraftPlotBulkActions } from './hooks/useDraftPlotBulkActions';
import { useDraftPlotsPanel } from './hooks/useDraftPlotsPanel';
import { useDraftPlotsTableColumns } from './hooks/useDraftPlotsTableColumns';
import { useExcelPlotSubmit } from './hooks/useExcelPlotSubmit';
import { useManualPlotSubmit } from './hooks/useManualPlotSubmit';
import { useMapCoordinatePreview } from './hooks/useMapCoordinatePreview';
import { usePlotPaths } from './hooks/usePlotPaths';
import { useSpreadsheetDropzone } from './hooks/useSpreadsheetDropzone';
import { useUploadTaskPoller } from './hooks/useUploadTaskPoller';
import { ExcelState, PanelState } from './types';
import { getExcelColumns } from './utils';

type ActivePanel = 'editGroup' | 'addGroup' | 'editPlot' | 'main';

function resolveActivePanel(
  panelState: PanelState,
  selectedGroupId: string | null,
  activePlotForEdit: DraftPlot | null,
): ActivePanel {
  if (panelState === 'editGroup' && selectedGroupId) return 'editGroup';
  if (panelState === 'addGroup') return 'addGroup';
  if (activePlotForEdit) return 'editPlot';
  return 'main';
}

const UploadCoordinatesPageInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cancelLabel = t('global.ui.buttons.cancel');
  const { plots } = usePlotPaths();

  const [panelState, setPanelState] = useState<PanelState>('options');
  const [selectedPlotId, setSelectedPlotId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const [manualName, setManualName] = useState('');
  const [manualLandType, setManualLandType] = useState<PlotType | ''>('');
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualError, setManualError] = useState<string | undefined>();

  const [analyzeError, setAnalyzeError] = useState<string | undefined>();
  const [excelState, setExcelState] = useState<ExcelState | null>(null);
  const [excelError, setExcelError] = useState<string | undefined>();
  const [uploadTaskId, setUploadTaskId] = useState<string | null>(null);
  const [uploadTaskError, setUploadTaskError] = useState<string | undefined>();
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isCommitConfirmOpen, setIsCommitConfirmOpen] = useState(false);

  const {
    organizationId,
    allDraftPlots,
    filteredPlots,
    refetchDraftPlots,
    panelWidth,
    mapRef,
    mapLoaded,
    setMapLoaded,
    hoveredPlot,
    hoveredCentroid,
    draftPlotsGeoJSON,
    draftPlotCentroidsGeoJSON,
    rowSelection,
    setRowSelection,
    sorting,
    setSorting,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    handleCommit,
    handleSaveDraftPlots,
    canCommit,
    isCommitting,
    handleDragHandleMouseDown,
    setHoveredPlotId,
    isPanelCollapsed,
    setIsPanelCollapsed,
  } = useDraftPlotsPanel(panelState, {
    focusedPlotId: selectedPlotId,
  });

  const organization = useOrganization({ id: organizationId }).data.results[0];

  const { data: groupsData, refetch: refetchGroups } = useGetDraftPlotGroups({
    queryParams: { organization: organizationId ?? undefined },
  });
  const allGroups = useMemo<DraftPlotGroup[]>(() => groupsData?.results ?? [], [groupsData?.results]);
  const groupedPlotIds = useMemo(() => new Set(allGroups.flatMap((group) => group.draft_plot_ids)), [allGroups]);
  const hasUngroupedPlots = useMemo(
    () => allDraftPlots.length === 0 || allDraftPlots.some((plot) => !groupedPlotIds.has(plot.id)),
    [allDraftPlots, groupedPlotIds],
  );
  const ungroupedFilteredPlots = useMemo(
    () => filteredPlots.filter((plot) => !groupedPlotIds.has(plot.id)),
    [filteredPlots, groupedPlotIds],
  );
  const filteredGroups = useMemo(
    () =>
      searchTerm ? allGroups.filter((group) => group.name.toLowerCase().includes(searchTerm.toLowerCase())) : allGroups,
    [allGroups, searchTerm],
  );

  const { handleDeleteOne, handleDiscardAll, isDiscarding } = useDraftPlotBulkActions({
    organizationId,
    remainingPlotCount: allDraftPlots.length,
    onEmpty: () => setPanelState('options'),
  });

  const tableColumns = useDraftPlotsTableColumns(handleDeleteOne);

  // An empty selection submits every draft plot, matching the existing "Save draft plots" fallback behavior.
  const selectedPlotIds = Object.entries(rowSelection)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const plotsToCommit =
    selectedPlotIds.length > 0 ? allDraftPlots.filter((plot) => selectedPlotIds.includes(plot.id)) : allDraftPlots;

  const { submit: saveCoordinates, isSubmitting: isSavingManual } = useManualPlotSubmit({
    organizationId,
    onSuccess: async (createdPlot) => {
      await refetchDraftPlots();
      setManualName('');
      setManualLat('');
      setManualLng('');
      setSelectedPlotId(createdPlot.id);
      setPanelState('editPlot');
    },
    onError: setManualError,
  });

  const { submit: submitExcel, isSubmitting: isSubmittingExcel } = useExcelPlotSubmit({
    organizationId,
    onSuccess: (taskId) => {
      setUploadTaskId(taskId);
      setExcelState(null);
      setPanelState('processing');
    },
    onError: setExcelError,
  });

  const { getRootProps, getInputProps, isDragActive, fileErrors, isAnalyzing } = useSpreadsheetDropzone({
    panelState,
    excelState,
    onAnalyzeSuccess: setExcelState,
    setAnalyzeError,
    t,
  });

  const { hasValidPreview, previewLat, previewLng } = useMapCoordinatePreview({
    mapRef,
    mapLoaded,
    lat: manualLat,
    lng: manualLng,
    active: panelState === 'manual',
  });

  const hasPlots = allDraftPlots.length > 0;
  useEffect(() => {
    if (panelState === 'options' && hasPlots) setPanelState('review');
  }, [panelState, hasPlots]);

  useUploadTaskPoller({
    taskId: uploadTaskId,
    isActive: panelState === 'processing',
    onSuccess: () => {
      setUploadTaskId(null);
      refetchDraftPlots();
      setPanelState('review');
    },
    onError: (message) => {
      setUploadTaskId(null);
      setUploadTaskError(message ?? undefined);
    },
  });

  const excelColumns = useMemo(() => getExcelColumns(excelState), [excelState]);

  const table = useReactTable({
    data: ungroupedFilteredPlots,
    columns: tableColumns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
    state: { sorting, rowSelection },
  });

  const pageStart = table.getState().pagination.pageIndex * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const isManualFormComplete = !!manualLandType && !!manualLat && !!manualLng;
  const isExcelFormComplete = !!excelState?.latColumn && !!excelState?.lngColumn && !!excelState?.landType;
  const activePlotForEdit =
    panelState === 'editPlot' ? (allDraftPlots.find((p) => p.id === selectedPlotId) ?? null) : null;
  const editingGroup = allGroups.find((g) => g.id === selectedGroupId) ?? null;
  const activePanel = resolveActivePanel(panelState, selectedGroupId, activePlotForEdit);

  const handleAddManualPlot = async () => {
    if (!isManualFormComplete) return;
    setManualError(undefined);
    await saveCoordinates({ name: manualName, lat: manualLat, lng: manualLng, landType: manualLandType });
  };

  const handleSubmitExcel = async () => {
    setExcelError(undefined);
    setUploadTaskError(undefined);
    if (excelState) await submitExcel(excelState);
  };

  const handleBackButtonClick = () => {
    if (panelState === 'manual') {
      setPanelState('options');
      return;
    }
    if (panelState === 'excel' && excelState) {
      setExcelState(null);
      return;
    }
    if (panelState === 'excel') {
      setPanelState('options');
      return;
    }
    navigate(-1);
  };

  return (
    <DraftPlotsPageLayout
      data-testid='upload-coordinates-page'
      panelWidth={panelWidth}
      isPanelCollapsed={isPanelCollapsed}
      onDragHandleMouseDown={handleDragHandleMouseDown}
      mapRef={mapRef}
      allDraftPlots={allDraftPlots}
      draftPlotsGeoJSON={draftPlotsGeoJSON}
      draftPlotCentroidsGeoJSON={draftPlotCentroidsGeoJSON}
      hoveredPlot={hoveredPlot}
      hoveredCentroid={hoveredCentroid}
      onMapLoad={() => setMapLoaded(true)}
      setHoveredPlotId={setHoveredPlotId}
      mapChildren={
        panelState === 'manual' &&
        hasValidPreview && (
          <Marker longitude={previewLng} latitude={previewLat} anchor='bottom'>
            <RiMapPinLine size={28} className='text-primary-100 drop-shadow' />
          </Marker>
        )
      }
    >
      {activePanel === 'editGroup' && editingGroup && (
        <GroupEditPanel
          group={editingGroup}
          plots={allDraftPlots}
          organizationId={organizationId ?? ''}
          onBack={() => {
            setSelectedGroupId(null);
            setPanelState('review');
          }}
          onDeleted={async () => {
            setSelectedGroupId(null);
            setPanelState('review');
            await Promise.all([refetchDraftPlots(), refetchGroups()]);
          }}
          onSaved={async () => {
            setSelectedGroupId(null);
            setPanelState('review');
            await refetchGroups();
          }}
          onRefetchGroups={refetchGroups}
          onEditPlot={(id) => {
            setSelectedPlotId(id);
            setPanelState('editPlot');
          }}
        />
      )}
      {activePanel === 'addGroup' && (
        <NewGroupPanel
          plots={allDraftPlots.filter((p) => rowSelection[p.id])}
          organizationId={organizationId ?? ''}
          onBack={() => setPanelState('review')}
          onSaved={async () => {
            setRowSelection({});
            setPanelState('review');
            await Promise.all([refetchDraftPlots(), refetchGroups()]);
          }}
        />
      )}
      {activePanel === 'editPlot' && activePlotForEdit && (
        <EditPlotPanel
          plot={activePlotForEdit}
          organizationId={organizationId ?? ''}
          hideBoundaryEdit
          onBack={() => {
            setSelectedPlotId(null);
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
          }}
          onDelete={(id) => {
            handleDeleteOne(id);
            setSelectedPlotId(null);
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
          }}
          onSaved={() => {
            setPanelState(selectedGroupId ? 'editGroup' : 'review');
            setSelectedPlotId(null);
            refetchDraftPlots();
          }}
        />
      )}
      {activePanel === 'main' && (
        <>
          <div className='shrink-0 border-b border-neutral-black-12 px-6 py-5'>
            {organization && <span className='typography-overline block text-text-secondary'>{organization.name}</span>}
            {panelState === 'manual' ? (
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <button
                    type='button'
                    aria-label='Back'
                    onClick={() => setPanelState('options')}
                    className='flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary'
                  >
                    <RiArrowLeftLine size={16} />
                  </button>
                  <h1 className='typography-h3'>{t('shared.uploadCoordinates.manual.title')}</h1>
                </div>
                <button
                  type='button'
                  onClick={() => navigate(-1)}
                  className='text-text-secondary hover:text-text-primary'
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className='flex items-center justify-between'>
                <h1 className='typography-h3'>{t('shared.uploadCoordinates.title')}</h1>
                <button
                  type='button'
                  aria-label={t('shared.uploadPlots.collapsePanelAriaLabel')}
                  onClick={() => setIsPanelCollapsed(true)}
                  className='text-text-secondary hover:text-text-primary'
                >
                  <RiCloseLine size={18} />
                </button>
              </div>
            )}
          </div>

          <div className='flex-1 overflow-y-auto px-6 py-5'>
            {panelState === 'options' && (
              <OptionsPanel
                t={t}
                onSelectManual={() => setPanelState('manual')}
                onSelectExcel={() => setPanelState('excel')}
              />
            )}
            {panelState === 'manual' && (
              <ManualPlotForm
                t={t}
                name={manualName}
                setName={setManualName}
                landType={manualLandType}
                setLandType={setManualLandType}
                lat={manualLat}
                setLat={setManualLat}
                lng={manualLng}
                setLng={setManualLng}
                error={manualError}
              />
            )}
            {panelState === 'excel' && (
              <ExcelUploadPanel
                t={t}
                excelState={excelState}
                setExcelState={setExcelState}
                excelColumns={excelColumns}
                isAnalyzing={isAnalyzing}
                isDragActive={isDragActive}
                fileErrors={fileErrors}
                analyzeError={analyzeError}
                excelError={excelError}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
              />
            )}
            {panelState === 'processing' && (
              <ProcessingPanel
                t={t}
                error={uploadTaskError}
                onBack={() => {
                  setUploadTaskError(undefined);
                  setPanelState('options');
                }}
              />
            )}
            {panelState === 'review' && (
              <CoordinatesReviewPanel
                t={t}
                allDraftPlots={allDraftPlots}
                filteredPlots={ungroupedFilteredPlots}
                groups={filteredGroups}
                hasUngroupedPlots={hasUngroupedPlots}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageStart={pageStart}
                pageEnd={pageEnd}
                table={table}
                isDiscarding={isDiscarding}
                onUploadMore={() => setPanelState('options')}
                onDiscardAll={() => setIsDiscardDialogOpen(true)}
                onRowClick={(id) => {
                  setSelectedPlotId(id);
                  setPanelState('editPlot');
                }}
                onSelectGroup={(id) => {
                  setSelectedGroupId(id);
                  setPanelState('editGroup');
                }}
              />
            )}
          </div>

          <CoordinatesFooterActions
            t={t}
            cancelLabel={cancelLabel}
            panelState={panelState}
            rowSelection={rowSelection}
            organizationId={organizationId ?? ''}
            excelState={excelState}
            isManualFormComplete={isManualFormComplete}
            isExcelFormComplete={isExcelFormComplete}
            isSavingManual={isSavingManual}
            isSubmittingExcel={isSubmittingExcel}
            canCommit={canCommit}
            onBack={handleBackButtonClick}
            onCancel={() => navigate(`${plots}?organizationId=${organizationId}`)}
            onAddManualPlot={handleAddManualPlot}
            onSubmitExcel={handleSubmitExcel}
            onCommit={() => setIsCommitConfirmOpen(true)}
            onSaveDraftPlots={handleSaveDraftPlots}
            onRefetch={refetchDraftPlots}
            onAddToGroup={() => setPanelState('addGroup')}
          />

          <ConfirmationDialog
            open={isDiscardDialogOpen}
            onOpenChange={setIsDiscardDialogOpen}
            header={t('shared.uploadPlots.review.discardAllConfirm.header')}
            body={t('shared.uploadPlots.review.discardAllConfirm.body')}
            CancelAction={
              <Button variant='outline' onClick={() => setIsDiscardDialogOpen(false)}>
                {cancelLabel}
              </Button>
            }
            PrimaryAction={
              <Button
                className='bg-error hover:bg-error-dark'
                loading={isDiscarding}
                onClick={() => {
                  setIsDiscardDialogOpen(false);
                  handleDiscardAll();
                }}
              >
                {t('shared.uploadPlots.review.discardAll')}
              </Button>
            }
          />

          <CommitConfirmationDialog
            open={isCommitConfirmOpen}
            onOpenChange={setIsCommitConfirmOpen}
            plotNames={plotsToCommit.map((plot) => plot.name)}
            isSubmitting={isCommitting}
            onConfirm={() => {
              setIsCommitConfirmOpen(false);
              handleCommit(plotsToCommit.map((plot) => plot.id));
            }}
          />
        </>
      )}
    </DraftPlotsPageLayout>
  );
};

export const UploadCoordinatesPage = withSuspenseBoundary(
  UploadCoordinatesPageInner,
  <Loader className='min-h-screen' />,
);
